/*
  FD brand: the sticky buy bar's behaviour.

  Below 821px this bar does not render at all. Its job down there is done by
  the forest "Add - price" SEGMENT beside the nav pill (treatment B, user ruling
  2026-09-06, mocks/glass-bottom-nav-b.html): one labelled control carrying the
  price the old glass slab carried, present from page load rather than waiting
  for the real button to scroll away. That is a skin and a host, not a second
  buy control -- the segment forwards to the same real submit button this bar
  has always forwarded to, so the product form, the line-item properties (Soft
  sit included), the add-on tiles and the basket drawer are untouched by it.

  Four jobs, all of them mirrors -- the bar owns no state of its own:
  - it shows ITSELF once the real basket button has been scrolled past, watched
    with an IntersectionObserver on that button rather than a scroll offset, so
    the bar and the button can never disagree about whether the button is on
    screen. The <=820px segment does NOT wait for that gate: it is a permanent
    part of the dock on a PDP, so `on` governs the desktop bar alone;
  - it copies the price block Dawn re-renders on every variant change, so the
    figure and the was/now gate are always the page's own, never recomputed,
    and it takes the variant NAME from Dawn's own `variant-change` event;
  - its button -- and, below 821px, the dock's Add segment -- forwards its click
    to the real one, and says so afterwards: the segment ticks and reads
    "Added", and the polite region beside the pill (wording: the header's
    `pill_added_label`) carries the same news to anyone who cannot see it;
  - it writes --fd-dock-extra, the ONLY part of --fd-bbar-h that CSS cannot
    know. With the slab retired that is now always 0px -- the dock row is the
    pill's own height -- but it is still written rather than assumed, so no
    stale value survives a rotate or a section swap.

  Presence is never animated away under prefers-reduced-motion: only the
  movement is dropped. Hiding the bar would remove a control, not an effect.
*/
if (!customElements.get('fd-buy-bar')) {
  customElements.define(
    'fd-buy-bar',
    class FdBuyBar extends HTMLElement {
      connectedCallback() {
        this.form = document.getElementById(this.dataset.formId);
        this.realCta = this.form?.querySelector('[type="submit"]');
        this.cta = this.querySelector('[data-fd-buybar-cta]');
        this.priceSlot = this.querySelector('[data-fd-buybar-price]');
        this.variantSlot = this.querySelector('[data-fd-buybar-variant]');
        this.priceSource = document.querySelector(this.dataset.priceSource);
        if (!this.realCta || !this.cta) return;

        // The pill's Add slot lives in the header section and ships hidden, so
        // it is inert on every page that has no product form. Adopting it here
        // keeps the markup (and its merchant-editable name) with the header and
        // the add logic with the buy bar.
        this.dock = window.matchMedia('(max-width: 820px)');
        this.resolvePill();
        // Delegated on the document rather than bound to the slot: the theme
        // editor re-renders the whole header section on any header setting
        // change, which would leave a listener bound to a node no longer in the
        // page. Delegation cannot go stale, and it costs nothing on the pages
        // (and the widths) where no slot exists -- the closest() misses.
        // It appears and disappears WITH the slab, on the same rule the bar has
        // always used: while the real basket button is still on screen there
        // must not be a second one. apply() owns the toggle.
        this.pillClickListener = (event) => {
          const slot = event.target?.closest?.('[data-fd-pill-add]');
          if (!slot || slot.disabled) return;
          this.realCta.click();
        };
        document.addEventListener('click', this.pillClickListener);
        // The state this bar WRITES onto the slot (hidden, disabled, name)
        // still needs a live reference, so re-take it -- and re-apply all of
        // it -- whenever a section is swapped in or out under us. Every branch
        // fails open: with no slot in the page the bar keeps its own button.
        this.sectionListener = () => {
          this.resolvePill();
          this.mirrorCta();
          this.mirror();
          this.apply(this.classList.contains('is-on'));
        };
        document.addEventListener('shopify:section:load', this.sectionListener);
        document.addEventListener('shopify:section:unload', this.sectionListener);

        this.mirrorCta();
        // The segment's price ships EMPTY from Liquid and this is its only
        // writer: the header renders before the product section, so the snippet
        // cannot know fd-main-product's fallback variant for a deep link to a
        // retired fabric. One read of the page's real price block at connect
        // fills it before anything is on screen -- apply() below keeps the
        // segment hidden until then -- and the observer keeps it there.
        this.mirror();
        this.cta.addEventListener('click', () => this.realCta.click());

        // Dawn replaces the price container's contents wholesale on a variant
        // change, so watching it is enough for both the figure and the gate.
        // Gated on the SOURCE alone: since treatment B this observer also keeps
        // the segment's price honest, and the segment must not stop repricing
        // because a desktop-only node is missing. mirror() guards each write.
        if (this.priceSource) {
          this.priceObserver = new MutationObserver(() => this.mirror());
          this.priceObserver.observe(this.priceSource, { childList: true, subtree: true, characterData: true });
        }
        this.ctaObserver = new MutationObserver(() => this.mirrorCta());
        this.ctaObserver.observe(this.realCta, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['disabled'],
        });

        // The variant NAME is not in the price block, so it comes from the
        // event product-info.js already publishes for it. Server-rendered on
        // first paint, so a deep link is right before any of this runs.
        if (this.variantSlot && typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.variantUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
            const variant = event?.data?.variant;
            if (variant) this.variantSlot.textContent = variant.title;
          });
          // A confirmation the shopper can see without leaving the pill. The
          // basket drawer is still the real receipt; this is the slot saying
          // the tap landed.
          this.cartUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
            // Only this page's own add -- a quantity change made inside the
            // drawer publishes here too and is not this slot's news.
            if (event?.source === 'product-form') this.flagAdded();
          });
        }

        if ('IntersectionObserver' in window) {
          this.visibility = new IntersectionObserver((entries) => {
            const entry = entries[0];
            this.apply(!entry.isIntersecting && entry.boundingClientRect.top < 0);
          });
          this.visibility.observe(this.realCta);
        }

        // The slab is one line taller when the title or the variant wraps, and
        // the dock has to keep pace or the page's last paragraph disappears
        // under it. matchMedia covers a rotate across the breakpoint.
        if ('ResizeObserver' in window) {
          this.sizeObserver = new ResizeObserver(() => this.measure());
          this.sizeObserver.observe(this);
        }
        // A rotate across 820px changes who owns the CTA, so re-run the whole
        // apply, not just the measurement.
        this.dockListener = () => this.apply(this.classList.contains('is-on'));
        this.dock.addEventListener('change', this.dockListener);

        this.apply(false);
      }

      disconnectedCallback() {
        this.priceObserver?.disconnect();
        this.ctaObserver?.disconnect();
        this.visibility?.disconnect();
        this.sizeObserver?.disconnect();
        this.variantUnsubscriber?.();
        this.cartUnsubscriber?.();
        if (this.dockListener) this.dock.removeEventListener('change', this.dockListener);
        if (this.pillClickListener) document.removeEventListener('click', this.pillClickListener);
        if (this.sectionListener) {
          document.removeEventListener('shopify:section:load', this.sectionListener);
          document.removeEventListener('shopify:section:unload', this.sectionListener);
        }
        window.clearTimeout(this.addedTimer);
        this.announce('');
        document.body.classList.remove('fd-has-buybar');
        document.documentElement.style.removeProperty('--fd-dock-extra');
        if (this.pillAdd) {
          this.pillAdd.hidden = true;
          this.pillAdd.classList.remove('is-added');
          delete this.pillAdd.dataset.state;
          if (this.pillAddWord) this.pillAddWord.textContent = this.pillAddDefaultWord;
          if (this.pillAddPrice) this.pillAddPrice.textContent = '';
        }
      }

      // Every read of the pill goes through here, so a header re-render is a
      // re-query rather than a stale node. Called on connect and on every
      // shopify:section:load / :unload.
      resolvePill() {
        this.pillAdd = document.querySelector('[data-fd-pill-add]');
        this.pillAddName = this.pillAdd?.querySelector('[data-fd-pill-add-name]');
        // Re-read rather than remembered: a re-rendered slot carries the
        // merchant's `pill_add_label` again, not the label mirrored onto the
        // node that has just been thrown away.
        this.pillAddDefaultName = this.pillAddName?.textContent.trim() || '';
        // The segment's VISIBLE parts (treatment B). All aria-hidden, so none
        // of them is the accessible name -- that stays the mirrored span above.
        this.pillAddWord = this.pillAdd?.querySelector('[data-fd-pill-add-word]');
        this.pillAddPrice = this.pillAdd?.querySelector('[data-fd-pill-add-price]');
        // Re-read, never remembered: a re-rendered segment carries the
        // merchant's `pill_add_word` again, not the "Sold out" that was
        // mirrored onto the node just thrown away.
        this.pillAddDefaultWord = this.pillAddWord?.textContent.trim() || '';
        this.pillLive = document.querySelector('[data-fd-pill-live]');
        this.pillAddedLabel = this.pillLive?.dataset.addedLabel?.trim() || '';
      }

      // The visible price, taken from the page's own price block and nowhere
      // else. .fd-price__now leads with a visually-hidden "Regular price" /
      // "Sale price" prefix for screen readers, so a bare textContent would put
      // those words on the segment: the clone drops them and leaves the figure
      // Shopify rendered through `money`.
      visiblePrice(now) {
        const clone = now.cloneNode(true);
        clone.querySelectorAll('.visually-hidden').forEach((node) => node.remove());
        return clone.textContent.trim();
      }

      // The polite region beside the pill. Writing '' first and the wording on
      // the next frame is what makes a second add of the same product announce
      // again -- an unchanged textContent is not a live-region update.
      announce(text) {
        if (!this.pillLive) return;
        window.cancelAnimationFrame(this.announceFrame);
        this.pillLive.textContent = '';
        if (!text) return;
        // Two frames, not one: some Safari+VoiceOver builds need a paint between
        // the clear and the rewrite before a clip-hidden node reads as "new"
        // (Wave 2b review). Single-call-site assumption: fd-buy-bar is rendered
        // once per PDP (fd-main-product.liquid); a second instance would double
        // the document click delegation above.
        this.announceFrame = window.requestAnimationFrame(() => {
          this.announceFrame = window.requestAnimationFrame(() => {
            if (this.pillLive) this.pillLive.textContent = text;
          });
        });
      }

      mirror() {
        const price = this.priceSource?.querySelector('.fd-price');
        if (!price) return;
        if (this.priceSlot) this.priceSlot.replaceChildren(price.cloneNode(true));
        // Treatment B: the segment carries the figure the slab used to. Same
        // source, same money filter, no arithmetic here -- and it is re-taken
        // after every Dawn re-render, so a colour change that reprices the
        // product reprices the segment with it.
        if (this.pillAddPrice) {
          const now = price.querySelector('.fd-price__now');
          this.pillAddPrice.textContent = now ? this.visiblePrice(now) : '';
        }
      }

      mirrorCta() {
        const label = this.realCta.textContent.trim();
        this.cta.textContent = label;
        this.cta.disabled = this.realCta.disabled;
        if (!this.pillAdd) return;
        // Icon-only slot: the label is its accessible name, so "Sold out"
        // travels to the pill exactly as it reads on the page.
        this.pillAdd.disabled = this.realCta.disabled;
        if (this.pillAddName) this.pillAddName.textContent = label || this.pillAddDefaultName;
        // ...and the VISIBLE word follows it when the real button is disabled,
        // so the segment says whatever the page says ("Sold out", "Unavailable")
        // rather than offering a price for something that cannot be bought. The
        // price and its dot are dropped by CSS off this one attribute, so no
        // second string is introduced here.
        if (this.realCta.disabled) {
          this.pillAdd.dataset.state = 'soldout';
          if (this.pillAddWord) this.pillAddWord.textContent = label || this.pillAddDefaultWord;
        } else {
          delete this.pillAdd.dataset.state;
          if (this.pillAddWord) this.pillAddWord.textContent = this.pillAddDefaultWord;
        }
      }

      flagAdded() {
        // Only while the slot is the live control: on desktop, and before the
        // real button has been scrolled past, the page's own button and the
        // basket drawer are the confirmation and this would be noise.
        if (!this.pillAdd || this.pillAdd.hidden) return;
        this.pillAdd.classList.add('is-added');
        this.announce(this.pillAddedLabel);
        window.clearTimeout(this.addedTimer);
        // One timer for both: the announcement is cleared out of the
        // accessibility tree exactly when the tick fades from the slot.
        this.addedTimer = window.setTimeout(() => {
          this.pillAdd?.classList.remove('is-added');
          this.announce('');
        }, 2200);
      }

      // --fd-dock-extra is what this slab adds ABOVE the pill: its own height
      // plus the gap between the two. The pill's own footprint is already in
      // --fd-bbar-h in CSS, and the safe-area inset is added by each consumer,
      // so neither belongs in this number.
      measure() {
        // Nothing docks above the pill any more. Treatment B retired the <=820px
        // glass slab (fd-pdp.css sets display:none on it) and moved its price
        // onto the segment INSIDE the dock row, whose height is already the
        // pill's -- so the extra this used to report is now always zero, and
        // --fd-bbar-h is the pill's own footprint again. Still written rather
        // than dropped, so a stale value from a previous state cannot survive
        // a rotate or a section swap.
        document.documentElement.style.setProperty('--fd-dock-extra', '0px');
      }

      apply(on) {
        this.classList.toggle('is-on', on);
        this.setAttribute('aria-hidden', String(!on));
        this.cta.tabIndex = on ? 0 : -1;
        // The clearance goes on the footer, not on <body>: body padding would
        // open a strip of bare page under the ink footer, whereas growing the
        // footer keeps its own ground running continuously behind the bar.
        document.body.classList.toggle('fd-has-buybar', on);
        // Desktop keeps its own button in the bar and never borrows the pill's
        // slot -- the slot's ancestor is display:none up there anyway, but
        // leaving the attribute alone keeps the desktop DOM untouched.
        if (this.pillAdd) {
          // Treatment B (user ruling 2026-09-06): on phones and tablets the
          // segment is there FROM PAGE LOAD, not once the real button has been
          // scrolled past -- it is a labelled buy control beside the pill, not
          // a stand-in that appears when the page's own button leaves. The
          // scroll gate still governs `on` (and therefore the desktop bar);
          // this branch simply no longer consults it. Desktop is untouched:
          // dock.matches is false above 820px, so the slot stays hidden and
          // the bar keeps its own button exactly as before.
          // ...and only once mirror() has actually put a figure in it: the
          // page's `price` block is theme-editor removable, and an unhidden
          // "Add · " with no price would be worse than no segment at all.
          const priced = !this.pillAddPrice || this.pillAddPrice.textContent.trim() !== '';
          const inPill = this.dock.matches && priced;
          this.pillAdd.hidden = !inPill;
          if (!inPill) {
            this.pillAdd.classList.remove('is-added');
            window.clearTimeout(this.addedTimer);
            this.announce('');
          }
        }
        this.measure();
      }
    }
  );
}
